import { Link } from 'react-router-dom'
import { AuthContext } from "../../Context"
import { Dialog, Transition } from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { Fragment, useState, useContext, useEffect } from 'react'
import { jsonConfig } from '../../Config'
import {
    HomeIcon,
    UserIcon,
    CommandLineIcon,
    UsersIcon,
    BanknotesIcon,
    UserCircleIcon,
    CircleStackIcon,
    CalendarDaysIcon,
    DocumentChartBarIcon,
} from '@heroicons/react/24/solid'

function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
}

const Sidebar = () => {
    const { states, setters } = useContext(AuthContext)
    const [navigation, setNavigation] = useState([])
    const [secondaryNavigation, setSecondaryNavigation] = useState([])
    const [firstLoad, setFirstLoad] = useState(true)

    let navigationData = [
        { name: 'Dashboard', href: '/dashboard', icon: HomeIcon, current: false },
        { name: 'Investidores', href: '/investidores', icon: UsersIcon, current: false },
        { name: 'Investimentos', href: '/investimentos', icon: BanknotesIcon, current: false },
        { name: 'Busca de dados', href: '/busca-dados', icon: CircleStackIcon, current: false },
        { name: 'Datas de exibição', href: '/datas-exibicao', icon: CalendarDaysIcon, current: false },
        { name: 'Relatórios', href: '/relatorios', icon: DocumentChartBarIcon, current: false },
        { name: 'Usuários', href: '/usuarios', icon: UserIcon, current: false },
        { name: 'Técnico', href: '/tecnicoIndex', icon: CommandLineIcon, current: false },
    ]

    if (localStorage.getItem('permisssion') !== 'ADMIN') {
        navigationData = navigationData.filter(
            (item) => item.href !== '/tecnicoIndex' && item.href !== '/usuarios' && item.href !== '/busca-dados' && item.href !== '/datas-exibicao' && item.href !== '/relatorios'
        )
    }

    let secondaryNavigationData = [
        { name: 'Perfil', href: '/perfil', icon: UserCircleIcon, current: false },
    ]

    if (localStorage.getItem('permisssion') === jsonConfig.roles.operacional) {
        secondaryNavigationData = []
    }

    const currentItem = (clicked) => {
        setNavigation(navigation.map((item) => ({ ...item, current: item.href === clicked.href })))
        setSecondaryNavigation(secondaryNavigation.map((item) => ({ ...item, current: false })))
    }

    const currentItemSecondary = (clicked) => {
        setSecondaryNavigation(secondaryNavigation.map((item) => ({ ...item, current: item.href === clicked.href })))
        setNavigation(navigation.map((item) => ({ ...item, current: false })))
    }

    const checkPath = () => {
        const path = `/${window.location.pathname.split('/')[1]}`
        if (path !== '/perfil') {
            currentItem({ href: path })
            setSecondaryNavigation((prev) => prev.map((item) => ({ ...item, current: false })))
        } else {
            setNavigation((prev) => prev.map((item) => ({ ...item, current: false })))
            currentItemSecondary({ href: path })
        }
    }

    useEffect(() => {
        setFirstLoad(true)
        setNavigation(navigationData)
        setSecondaryNavigation(secondaryNavigationData)
        return () => { setNavigation([]); setSecondaryNavigation([]); setFirstLoad(true) }
    }, [])

    useEffect(() => {
        if (firstLoad && navigation.length > 0 && secondaryNavigation.length > 0) {
            checkPath()
            setFirstLoad(false)
        }
    }, [navigation, secondaryNavigation])

    useEffect(() => {
        if (navigation.length > 0 && secondaryNavigation.length > 0) checkPath()
    }, [window.location.pathname])

    const NavItem = ({ item, onClick }) => (
        <li>
            <Link
                to={item.href}
                onClick={onClick}
                className={classNames(
                    item.current
                        ? 'bg-brand-yellow text-gray-900 font-semibold'
                        : 'text-gray-300 hover:text-white hover:bg-white/10',
                    'group flex items-center gap-x-3 rounded-lg py-2.5 px-3 text-sm font-medium transition-all duration-150'
                )}
            >
                <item.icon className={classNames(item.current ? 'text-gray-900' : 'text-gray-400 group-hover:text-white', 'h-5 w-5 shrink-0')} aria-hidden="true" />
                {item.name}
            </Link>
        </li>
    )

    const SidebarContent = ({ onItemClick }) => (
        <div className="flex flex-grow flex-col bg-gray-900 overflow-y-auto border-r border-gray-700 shadow-xl">
            <div className="flex items-center justify-center px-4 py-5 bg-white border-b-4 border-brand-yellow">
                <img className="h-16 w-auto" src={`${jsonConfig.urlReact}/logo.png`} alt="Matriz MG" />
            </div>

            <nav className="flex flex-1 flex-col px-3 py-6 gap-y-6">
                <div>
                    <p className="px-3 mb-2 text-[10px] font-semibold uppercase tracking-widest text-gray-500">Menu</p>
                    <ul role="list" className="space-y-0.5">
                        {navigation.map((item) => (
                            <NavItem key={item.name} item={item} onClick={() => { currentItem(item); onItemClick?.() }} />
                        ))}
                    </ul>
                </div>

                {secondaryNavigation.length > 0 && (
                    <div className="mt-auto">
                        <div className="border-t border-white/10 pt-4">
                            <p className="px-3 mb-2 text-[10px] font-semibold uppercase tracking-widest text-gray-500">Conta</p>
                            <ul role="list" className="space-y-0.5">
                                {secondaryNavigation.map((item) => (
                                    <NavItem key={item.name} item={item} onClick={() => { currentItemSecondary(item); onItemClick?.() }} />
                                ))}
                            </ul>
                        </div>
                    </div>
                )}
            </nav>
        </div>
    )

    return (
        <>
            {!states.sideBar && (
                <div className="hidden md:fixed md:inset-y-0 md:flex md:w-64 md:flex-col">
                    <SidebarContent />
                </div>
            )}

            <Transition.Root show={states.sideBar} as={Fragment}>
                <Dialog as="div" className="relative z-40 lg:hidden" onClose={setters.setSideBar}>
                    <Transition.Child
                        as={Fragment}
                        enter="transition-opacity ease-linear duration-300" enterFrom="opacity-0" enterTo="opacity-100"
                        leave="transition-opacity ease-linear duration-300" leaveFrom="opacity-100" leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black/60" />
                    </Transition.Child>

                    <div className="fixed inset-0 z-40 flex">
                        <Transition.Child
                            as={Fragment}
                            enter="transition ease-in-out duration-300 transform" enterFrom="-translate-x-full" enterTo="translate-x-0"
                            leave="transition ease-in-out duration-300 transform" leaveFrom="translate-x-0" leaveTo="-translate-x-full"
                        >
                            <Dialog.Panel className="relative flex w-full max-w-xs flex-1 flex-col">
                                <Transition.Child
                                    as={Fragment}
                                    enter="ease-in-out duration-300" enterFrom="opacity-0" enterTo="opacity-100"
                                    leave="ease-in-out duration-300" leaveFrom="opacity-100" leaveTo="opacity-0"
                                >
                                    <div className="absolute top-2 right-0 -mr-12">
                                        <button
                                            type="button"
                                            className="flex h-10 w-10 items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-brand-yellow"
                                            onClick={() => setters.setSideBar(false)}
                                        >
                                            <XMarkIcon className="h-6 w-6 text-white" aria-hidden="true" />
                                        </button>
                                    </div>
                                </Transition.Child>
                                <SidebarContent onItemClick={() => setters.setSideBar(false)} />
                            </Dialog.Panel>
                        </Transition.Child>
                        <div className="w-14 flex-shrink-0" aria-hidden="true" />
                    </div>
                </Dialog>
            </Transition.Root>
        </>
    )
}

export default Sidebar
